/* eslint-disable @next/next/no-img-element */
import parse from 'html-react-parser';

//components
import { Container } from '@/components/Grid';
import {
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';

import { FreightExperts } from '@/containers-latam/FreightExperts';
import { PillLink } from '@/containers-ai/Buttons';
import { CountUp } from '@/containers-latam/CountUp';

import { PlatformScale } from '@/containers-latam/PlatformScale';

//styles
import {
  LiveSection,
  BigProof,
  BigProofValue,
  BigProofText,
  TaskGrid,
  TaskCard,
  TaskTitle,
  TaskDescription,
  TaskMetricWrap,
  TaskMetricText,
  TaskMetric,
  TaskMetricLabel,
  TaskCustomerBox,
  TaskCustomerLogo,
  CustomCard,
  CustomCardKicker,
  CustomCardTitle,
  CustomCardCopy,
  PlusDivider,
  ExceptionsBanner,
  ExceptionsDot,
} from './LiveInProduction.styles';

// Order these slugs render in — matches the JSON keys in content.tasks.
const TASK_SLUGS = [
  'document_collection_and_filing',
  'tracking_and_status_updates',
  'tender_failures',
  'proactive_load_audits',
  'claims_processing',
  'scheduling_and_re_scheduling',
];

// LATAM pages pass `taskHrefs` (slug -> localized agent page), `ctaHref`,
// `numberLocale`, and drop the full Freight Experts block in favor of a banner.
export function LiveInProduction({
  content,
  freightExpertsContent,
  platformScaleContent,
  taskHrefs,
  ctaHref = '/contact/#contact-form',
  numberLocale,
}) {
  const tasks = TASK_SLUGS.map((slug) => ({
    slug: slug.replace(/_/g, '-'),
    href: taskHrefs?.[slug],
    customer: null,
    ...content.tasks[slug],
  }));

  return (
    <LiveSection id="ai">
      <Container>
        <Kicker>
          <KickerDot />
          {content.kicker}
        </Kicker>
        <SectionTitle style={{ maxWidth: '905px' }}>
          {content.title}
          <br />
          <Grad>{content.titleHighlight}</Grad>
        </SectionTitle>
        <SectionLede>{content.lede}</SectionLede>

        <BigProof>
          <BigProofValue>
            {content.bigProof.prefix}
            <CountUp value={content.bigProof.value} locale={numberLocale} />
            {content.bigProof.suffix ?? '+'}
          </BigProofValue>
          <BigProofText>{parse(content.bigProof.copy)}</BigProofText>
        </BigProof>

        {content.exceptionsBanner && (
          <ExceptionsBanner>
            <ExceptionsDot />
            {content.exceptionsBanner}
          </ExceptionsBanner>
        )}

        <TaskGrid>
          {tasks.map((task) => (
            <TaskCard key={task.title} href={task.href ?? `/ai/${task.slug}/`}>
              <TaskTitle>{task.title}</TaskTitle>
              <TaskDescription>{task.description}</TaskDescription>
              <TaskMetricWrap>
                <TaskMetricText>
                  <TaskMetric>{task.metric}</TaskMetric>
                  <TaskMetricLabel>{task.label}</TaskMetricLabel>
                </TaskMetricText>
                {task.customer && (
                  <TaskCustomerBox>
                    <TaskCustomerLogo
                      src={task.customer.img}
                      alt={task.customer.alt}
                    />
                  </TaskCustomerBox>
                )}
              </TaskMetricWrap>
            </TaskCard>
          ))}
        </TaskGrid>

        <CustomCard>
          <div>
            <CustomCardKicker>{content.customCard.kicker}</CustomCardKicker>
            <CustomCardTitle>{content.customCard.title}</CustomCardTitle>
            <CustomCardCopy>{content.customCard.copy}</CustomCardCopy>
          </div>
          <PillLink href={ctaHref} className="qlf-btn-2">
            {content.customCard.ctaLabel}
          </PillLink>
        </CustomCard>

        {freightExpertsContent && (
          <>
            <PlusDivider aria-hidden="true">+</PlusDivider>

            <FreightExperts border={false} content={freightExpertsContent} />
          </>
        )}

        <PlatformScale
          stats={Object.values(platformScaleContent.stats)}
          label={platformScaleContent.label}
          locale={numberLocale}
        />
      </Container>
    </LiveSection>
  );
}
